import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { TagChips } from '../../components/common/EventTags';
import CompanyEventRow from './CompanyEventRow';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';

/* What my employer is running, and whether I said I'd be there.

   The employee half of the feature, and the only screen in it that is not behind the admin gate —
   anyone who works for a company holding the engagement product sees their own company's
   published events here, and nobody else's, ever.

   Two buttons per row: I'm coming, I'm not coming. The second one is the point. Every tool can
   record a yes; a townhall is planned around the noes, and until now the platform had nowhere at
   all to put one.

   ★ "How many are coming" only renders when the event's owner switched it on — and it is absent
   from the payload otherwise, not merely undrawn. */

const CompanyCalendar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    const q = picked.map((id) => `tag=${encodeURIComponent(id)}`).join('&');
    apiRequest('GET', `/company/calendar${q ? `?${q}` : ''}`)
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [picked, navigate]);

  useEffect(() => { load(); }, [load]);

  const answer = (eventId, status) => {
    if (busy) return;
    setBusy(eventId);
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/rsvp`, { status })
      .then(() => load())
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(null));
  };

  const toggleTag = (id) => setPicked((list) =>
    (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const events = (data && data.events) || [];
  // How many events carry each category. From the API, not counted here: the list below is
  // already narrowed by the filter, so counting it would make the numbers shrink as you click.
  const tagCounts = (data && data.tag_counts) || {};
  const upcoming = events.filter((e) => !e.is_past);
  const past = events.filter((e) => e.is_past);

  const row = (e) => (
    <CompanyEventRow
      key={e.id}
      event={e}
      lang={i18n.language}
      onOpen={() => navigate(`/public-event/${e.id}`)}
      // The name, its categories and the headcount belong together on the top line.
      badges={(
        <>
          {/* An event of one of MY clubs, in the same diary as the company's own. */}
          {e.club && <span className="ce-pill"><Icon name="users" /> {e.club.name}</span>}
          {e.rsvp && (
            <span className="ce-pill ce-pill--going">
              {t('goingCount', { n: e.rsvp.going })}
            </span>
          )}
          {e.my_rsvp === 'going' && (
            <span className="bravos-event__pill bravos-event__pill--in">
              {t('youAreComing')}
            </span>
          )}
        </>
      )}
    >
      {/* The answer buttons get their own row underneath. Hidden once the deadline has passed —
          the server refuses either way, and a button that only ever errors is worse than none. */}
      {!e.is_past && !e.registration_closed && (
        <>
          <button
            type="button"
            className={`ce-tab ${e.my_rsvp === 'going' ? 'is-on' : ''}`}
            onClick={(ev) => { ev.stopPropagation(); answer(e.id, 'going'); }}
            disabled={busy === e.id}
          >
            <Icon name="check" /> {t('imComing')}
          </button>
          <button
            type="button"
            className={`ce-tab ${e.my_rsvp === 'not_going' ? 'is-on' : ''}`}
            onClick={(ev) => { ev.stopPropagation(); answer(e.id, 'not_going'); }}
            disabled={busy === e.id}
          >
            {t('imNotComing')}
          </button>
        </>
      )}
      {!e.is_past && e.registration_closed && (
        <span className="ce-note">{t('registrationClosed')}</span>
      )}
    </CompanyEventRow>
  );

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="calendar" theme="primary" className="me-2" />
          {t('ourEvents')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            {/* The category filter, with the way IN to managing the catalogue sitting right at
                the end of it — that is where you are when you notice a category is missing.
                Admins only: an employee filters by categories, they do not own them. */}
            {((data.tags || []).length > 0 || data.can_manage_tags) && (
              <div className="ce-tagrow">
                <TagChips tags={data.tags || []} selected={picked} onToggle={toggleTag}
                          counts={tagCounts} />
                {data.can_manage_tags && (
                  <button
                    type="button"
                    className="etag etag--btn ce-tagadd"
                    onClick={() => navigate('/event-categories')}
                    title={t('eventTags')}
                  >
                    <Icon name="plus" />
                  </button>
                )}
              </div>
            )}
            {notice && <p className="bravos-cal__notice">{notice}</p>}

            {events.length === 0 && <p className="ce-note">{t('noCompanyEvents')}</p>}

            {upcoming.length > 0 && (
              <>
                <p className="bravos-cal__label">{t('upcoming')}</p>
                {upcoming.map(row)}
              </>
            )}
            {past.length > 0 && (
              <>
                <p className="bravos-cal__label bravos-cal__label--past">{t('past')}</p>
                {past.map(row)}
              </>
            )}

            {/* The survey library. It belongs with the events — a survey exists to be run on
                one — and not with the programmes, which are how the year is planned. Admins
                only: an employee answers surveys, they do not keep the library. */}
            {data.can_manage_tags && (
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => navigate('/company-surveys')}
              >
                <Icon name="document" /> {t('ourSurveys')} <Icon name="angle-right" />
              </button>
            )}
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanyCalendar;
